import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import axios from "axios";

import MDBox from "components/MDBox";
import StatusBadge from "components/ReportUI/Statusbadge";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { AccountsContext } from "context/AccountsContext";

import { trackingReportEndpoint } from "config";
import TrackingReportTable from "./trackingreporttable";
import SingleDatePicker from "layouts/SingleDatePicker/singleDatePicker";
import DhundhooLoader from "../Loader/Loader";

function TrackingReport() {
    const [reportData, setReportData] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [successSB, setSuccessSB] = useState(false);
    const [errorSB, setErrorSB] = useState(false);
    const [vehicleId, setVehicleId] = useState("");
    const [loading, setLoading] = useState(false);

    // ✅ Aaj (Today) ki date ko YYYY-MM-DD format me nikalna

    const formatDisplayDateTime = (dateString) => {
        if (!dateString) return "";

        const date = new Date(dateString);

        return date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",   // Jun
            day: "2-digit",   // 03
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true      // AM/PM
        });
    };

    const getTodayDateString = () => {
        const today = new Date();
        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const defaultDate = getTodayDateString();

    // ✅ Set to null by default for placeholder
    const [date, setDate] = useState(defaultDate);
    const [interval, setInterval] = useState("None");

    const { fetchThings, things } = useContext(AccountsContext);

    useEffect(() => {
        fetchThings();
    }, []);

    // Snackbar
    const openSuccessSB = () => setSuccessSB(true);
    const closeSuccessSB = () => setSuccessSB(false);
    const openErrorSB = () => setErrorSB(true);
    const closeErrorSB = () => setErrorSB(false);

    // Columns for Tracking Report
    const columns = [
        {
            Header: "Date",
            accessor: "lastTrackedTime",
            Cell: ({ value }) => formatDisplayDateTime(value),
            width: "20%",
        },
        { Header: "Vehicle Number", accessor: "thingId", width: "20%" },
        { Header: "Speed", accessor: "speed", align: "center" },
        {
            Header: "Ignition Status",
            accessor: "ignitionStatus",
            align: "center",
            Cell: ({ value }) => (
                <StatusBadge value={value} trueLabel="ON" falseLabel="OFF" trueValues={["on", "true", "1", "running"]} />
            ),
        },
        {
            Header: "Location",
            accessor: "locationLink",
            Cell: (cell) => {
                const value = cell.value;  // safer
                return value ? (
                    <a href={value} target="_blank" rel="noopener noreferrer">
                        📍
                    </a>
                ) : (
                    "-"
                );
            },
        }
    ];

    // ✅ New Formatter: Converts "YYYY-MM-DD" to "YYYY/MM/DD"
    const formatApiDate = (dateString) => {
        if (!dateString) return "";
        return dateString.replace(/-/g, "/");
    };

    const fetchReport = async () => {
        if (!vehicleId) {
            setErrorMessage("Please select a vehicle");
            openErrorSB();
            return;
        }

        // ✅ Validation for date
        if (!date) {
            setErrorMessage("Please select a date first");
            openErrorSB();
            return;
        }

        const token = localStorage.getItem("token");
        setLoading(true);
        setErrorMessage("");

        try {
            const formattedDate = formatApiDate(date); // Convert to YYYY/MM/DD

            const data = {
                date: formattedDate,
                zone: "Asia/Kolkata",
                startTime: `${formattedDate} 00:00`,
                endTime: `${formattedDate} 23:59`,
                things: vehicleId,
                thingIds: [vehicleId],
                geofence: [],
                groups: [],
                interval: interval === "None" ? null : interval,
            };

            const config = {
                method: "post",
                url: trackingReportEndpoint,
                headers: {
                    "x-dhundhoo-session": token,
                    "Content-Type": "application/json",
                },
                data,
            };

            const response = await axios.request(config);
            setReportData(response.data);
            openSuccessSB();
        } catch (error) {
            console.error(error);
            setErrorMessage("Error loading tracking report");
            openErrorSB();
        } finally {
            setLoading(false);
        }
    };

    const clearFilters = () => {
        setVehicleId("");
        setReportData([]);
        setErrorMessage("");
        setDate(defaultDate); // ✅ Reset back to today's date
        setInterval("None");
    };

    const exportToCSV = () => {
        if (reportData.length === 0) {
            setErrorMessage("No data to export");
            openErrorSB();
            return;
        }



        const formattedDate = date || "export";

        const headers = ["Date", "Vehicle Number", "Speed", "Ignition Status", "Location Link"];

        const rows = reportData.map((row) => [
            row.lastTrackedTime,
            row.thingId,
            row.speed,
            row.ignitionStatus,
            row.locationLink
        ].join(","));

        const csvContent = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `TrackingReport_${formattedDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox p={3} pb={0}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <Grid container spacing={2}>
                                        {/* Vehicle Select */}
                                        <Grid item xs={12} sm={6} md={3}>
                                            <Autocomplete
                                                options={things || []}
                                                getOptionLabel={(option) => option.thingId || ""}
                                                filterOptions={(options, { inputValue }) => {
                                                    const searchValue = inputValue.toLowerCase().trim();

                                                    return options.filter((option) => {
                                                        const name = (option.name || "").toLowerCase();
                                                        const nickName = (option.nickName || "").toLowerCase();
                                                        const thingId = (option.thingId || "").toLowerCase();

                                                        return (
                                                            name.includes(searchValue) ||
                                                            nickName.includes(searchValue) ||
                                                            thingId.includes(searchValue)
                                                        );
                                                    });
                                                }}
                                                value={things?.find((t) => t.thingId === vehicleId) || null}
                                                onChange={(event, newValue) => {
                                                    setVehicleId(newValue ? newValue.thingId : "");
                                                }}
                                                renderOption={(props, option) => (
                                                    <li
                                                        {...props}
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            width: "100%",
                                                        }}
                                                    >
                                                        <span>{option.nickName || option.name || "-"}</span>

                                                        <span style={{ color: "#666" }}>
                                                            {option.thingId}
                                                        </span>
                                                    </li>
                                                )}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        placeholder="Search Vehicle"
                                                        fullWidth
                                                        size="small"
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                                    borderColor: "#FB8C00",
                                                                },
                                                            },
                                                            "& .MuiInputLabel-root.Mui-focused": {
                                                                color: "#FB8C00",
                                                            },
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>

                                        {/* ✅ Replaced DatePicker with SingleDatePicker */}
                                        <Grid item xs={12} sm={6} md={3}>
                                            <div style={{ width: '70%' }}>
                                                <SingleDatePicker
                                                    date={date}
                                                    onChange={(newDate) => setDate(newDate)}
                                                    disabled={loading}
                                                />
                                            </div>
                                        </Grid>

                                        <Grid item xs={6} sm={3} md={2}>
                                            <Autocomplete
                                                fullWidth
                                                size="small"
                                                options={["None", 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60]}
                                                value={interval}
                                                onChange={(e, newValue) => setInterval(newValue)}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Interval (sec)" variant="outlined"
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                                    borderColor: "#FB8C00", // ✅ border on click
                                                                },
                                                            },
                                                            "& .MuiInputLabel-root.Mui-focused": {
                                                                color: "#FB8C00", // ✅ label color (if label used)
                                                            },
                                                        }}
                                                    />
                                                )}
                                            />
                                        </Grid>


                                        {/* Buttons */}
                                        <Grid item xs={4} sm={2} md={1}>
                                            <MDButton
                                                fullWidth
                                                variant="gradient"
                                                color="warning"
                                                size="small"
                                                onClick={fetchReport}
                                                disabled={loading}
                                            >
                                                {loading ? <CircularProgress size={20} color="inherit" /> : "LOAD"}
                                            </MDButton>
                                        </Grid>

                                        <Grid item xs={4} sm={2} md={1}>
                                            <MDButton
                                                fullWidth
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={clearFilters}
                                                disabled={loading}
                                            >
                                                CLEAR
                                            </MDButton>
                                        </Grid>

                                        <Grid item xs={4} sm={2} md={1}>
                                            <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading || reportData.length === 0} />
                                        </Grid>
                                    </Grid>
                                </LocalizationProvider>
                            </MDBox>

                            {/* Error Alert */}
                            {errorMessage && (
                                <MDBox px={2} pt={2}>
                                    <Alert severity="error">{errorMessage}</Alert>
                                </MDBox>
                            )}

                            {/* Loader or Table */}
                            <MDBox pt={3} display="flex" justifyContent="center">
                                {loading ? (
                                    <DhundhooLoader />
                                ) : (
                                    <TrackingReportTable
                                        table={{ columns, rows: reportData }}
                                        isSorted={true}
                                        entriesPerPage={true}
                                        showTotalEntries={true}
                                        noEndBorder
                                    />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            {/* Snackbars */}
            <MDSnackbar
                color="success"
                icon="check"
                title="Tracking Report"
                content="Data Loaded Successfully"
                open={successSB}
                onClose={closeSuccessSB}
                close={closeSuccessSB}
                bgWhite
            />
            <MDSnackbar
                color="error"
                icon="warning"
                title="Tracking Report"
                content="Failed to load report"
                open={errorSB}
                onClose={closeErrorSB}
                close={closeErrorSB}
                bgWhite
            />
        </DashboardLayout>
    );
}

export default TrackingReport;